import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { Aws } from '../aws/aws';

interface RoomPlanPlugin {
  isSupported(): Promise<{
    supported: boolean;
    multiRoomSupported: boolean;
    reason?: string;
  }>;
  startScan(): Promise<{
    success: boolean;
    roomCount: number;
    usdzPath?: string;
    jsonPath?: string;
  }>;
  getScanResults(): Promise<{
    success: boolean;
    roomCount: number;
    usdzPath?: string;
    jsonPath?: string;
  }>;
  openARQuickLook(options: { url: string }): Promise<void>;
}

const RoomPlan = registerPlugin<RoomPlanPlugin>('RoomPlan');

export interface RoomScanResult {
  success: boolean;
  roomCount: number;
  usdzPath?: string;
  jsonPath?: string;
  usdzUrl?: string;
  jsonUrl?: string;
}

export interface RoomScanSupport {
  supported: boolean;
  multiRoomSupported: boolean;
  reason?: string;
}

@Injectable()
export class RoomScanService {

  constructor(
    private platform: Platform,
    private aws: Aws
  ) {}

  async isSupported(): Promise<RoomScanSupport> {
    if (!this.platform.is('ios') || !this.platform.is('capacitor')) {
      return {
        supported: false,
        multiRoomSupported: false,
        reason: 'AR Room Scanning is only available on iOS devices with the native app.'
      };
    }

    try {
      return await RoomPlan.isSupported();
    } catch (error) {
      return {
        supported: false,
        multiRoomSupported: false,
        reason: `Failed to check RoomPlan support: ${(error as Error).message}`
      };
    }
  }

  async startScan(): Promise<RoomScanResult> {
    const support = await this.isSupported();
    if (!support.supported) {
      throw new Error(support.reason || 'AR Room Scanning is not supported on this device.');
    }

    return await RoomPlan.startScan();
  }

  async getScanResults(): Promise<RoomScanResult> {
    return await RoomPlan.getScanResults();
  }

  async openARQuickLook(url: string): Promise<void> {
    try {
      if (this.platform.is('ios') && this.platform.is('capacitor')) {
        return await RoomPlan.openARQuickLook({ url });
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening AR Quick Look:', error);
      throw error;
    }
  }

  async uploadScanResults(result: RoomScanResult, addressId: number): Promise<RoomScanResult> {
    if (!result.success) {
      return result;
    }

    const timestamp = Date.now();
    const updatedResult = { ...result };

    if (result.usdzPath) {
      try {
        const webUrl = Capacitor.convertFileSrc(result.usdzPath);
        const response = await fetch(webUrl);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        const s3Key = `addresses/${addressId}/scans/scan_${timestamp}.usdz`;
        const uploadResp = await this.aws.uploadFileToS3(uint8Array, s3Key, 'model/vnd.usdz+zip');
        updatedResult.usdzUrl = uploadResp.Location;
      } catch (error) {
        console.error('Error uploading USDZ to S3:', error);
      }
    }

    if (result.jsonPath) {
      try {
        const webUrl = Capacitor.convertFileSrc(result.jsonPath);
        const response = await fetch(webUrl);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        const s3Key = `addresses/${addressId}/scans/scan_${timestamp}.json`;
        const uploadResp = await this.aws.uploadFileToS3(uint8Array, s3Key, 'application/json');
        updatedResult.jsonUrl = uploadResp.Location;
      } catch (error) {
        console.error('Error uploading JSON to S3:', error);
      }
    }

    return updatedResult;
  }

  isIOS(): boolean {
    return this.platform.is('ios');
  }

  isNativeApp(): boolean {
    return this.platform.is('capacitor');
  }
}
